import { computed, Injectable, signal } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, from } from 'rxjs';
import { Auth } from '@angular/fire/auth';
import { AuthService } from './auth.service';
import { DigitalCard } from '../pages/cards/cards.component';
import { environment } from 'src/environments/environment';

// API Request/Response Interfaces
export interface CardCreateRequest {
contactName: string;    // ✅ Changed from 'name'
  contactPhone: string;
  handler: string;
contactEmail: string;   // ✅ Changed from 'email'  
  type?: string;
  uid?: string;
  industry?: string;
  language?: string;
  businessName?: string;
  // Optional link arrays that can be provided during creation
  quickLinks?: quick_links[];
  SocialLinks?: social_links[];
}

// Simplified interface for create form
export interface CreateCardForm {
  // General
  name: string;         // Used for fullName in personalInfo
  handler: string;
  language: string;
  type: string;
businessName?: string;
  // Contact
  phone: string;
  email: string;
  website?: string;

  // Personal Info
  address?: string;
  dob?: string;         // date of birth
  gender?: string;      // male/female/other
  photo?: string;       // profile picture URL
  age?: number;

  // Business Info
  companyName?: string;
  jobTitle?: string;    // maps to position in backend
  department?: string;
  industry?: string;
  location?: string;
}


export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
}

// Enhanced error handling interfaces
export interface BusinessError {
  type: 'validation' | 'network' | 'auth' | 'business' | 'server' | 'license';
  title: string;
  message: string;
  details?: string;
  code?: string;
}

// Domain Model Interfaces based on DiGivCards API Schema
export interface PersonalInfo {
  dob?: string;
  gender?: string;
  profilePicture?: string;
  age?: number | string;
  address?: string;
  mobile?: string;
  fullName?: string;
  company?: string;
}


export interface BusinessInfo {
  businessName?: string;
  description?: string;
  companyWebsite?: string;
  companyPhone?: string;
  companyEmail?: string;
  companyAddress?: string;
  companyLogo?: string; 
  logoShape?: string| null; 
}



export interface Address {
  street: string;
  city: string;
  state: string;
  country: string;
  pincode: string;
  landmark?: string;
  area?: string;
}

export interface Contact {
  email: string;
  phone: string;
  whatsapp?: string;
  alternateEmail?: string;
  alternatePhone?: string;
  department?: string;
  jobTitle?: string;
  fax?: string;
  
   contactName?: string; 
   primaryEmail?: string;
   primaryPhone?: string;
   whatsappNumber?: string;
}

export interface social_links {
  platform: string; // e.g., "Facebook", "LinkedIn", "Twitter"
  url: string;   // optional, e.g., "bi-facebook"
}

export interface CardImage {
  url: string;
  thumbnailUrl?: string;
  altText?: string;
  [key: string]: any;
}
export interface Leader {
  name: string;
  role: string;
  photoUrl?: string;
}

export interface Permissions {
  show_Analytical: 'show' | 'hide';
  Show_Payment_Methods: 'show' | 'hide';
}

export interface Branches {
  city: string;
  address: string;
  pincode: string;
}

export interface Entity {
  name: string;
  domainUrl: string;
}



// You can convert social_links → social_links[] dynamically if needed
export function mapSocialLinks(links: social_links): social_links[] {
  const result: social_links[] = [];
  if (!links) return result;

  Object.entries(links).forEach(([platform, url]) => {
    if (url) {
      result.push({ platform, url });
    }
  });

  return result;
}

export interface quick_links {
  title: string;      // Changed from 'name' to 'title'
  url: string; // Added to match your backend
}

export interface Service {
  id?: string;
  title: string;
  description: string;
  price?: number;
  currency?: string;
  duration?: string;
  category?: string;
  image?: string;
  isActive?: boolean;
  order?: number;
}

export interface Product {
  id?: string;
  name: string;
  description: string;
  price: number;
  currency?: string;
  category?: string;
  images?: string[];
  isActive?: boolean;
  order?: number;
  sku?: string;
  stock?: number;
}

export interface UPIPayment {
  upiId: string;
  name: string;
  merchantCode?: string;
  isActive?: boolean;
}

export interface BankDetails {
  accountNumber: string;
  ifscCode: string;
  bankName: string;
  accountHolderName: string;
  accountType?: string;
  branchName?: string;
  isActive?: boolean;
}

export interface PaymentGateway {
  gatewayType: 'razorpay' | 'stripe' | 'paypal' | 'paytm';
  merchantId: string;
  merchantKey?: string;
  publicKey?: string;
  isActive?: boolean;
  testMode?: boolean;
}

export interface ThemeColors {
  primary: string;
  secondary: string;
  accent: string;
  background?: string;
  text?: string;
  cardBackground?: string;
  gradientStart?: string;
  gradientEnd?: string;
}

export interface Event {
  id?: string;
  title: string;
  description: string;
  startDate: string;
  endDate?: string;
  location?: string;
  eventType?: 'meeting' | 'conference' | 'workshop' | 'seminar' | 'other';
  isActive?: boolean;
  registrationUrl?: string;
  price?: number;
  currency?: string;
}

@Injectable({
  providedIn: 'root'
})
export class CardsService {
  private readonly baseUrl = environment.apiUrl;
  private cardsState = signal<DigitalCard[]>([]);
  private selectedCardState = signal<DigitalCard | null>(null);
  private loadingState = signal<boolean>(false);
  private errorState = signal<string | null>(null);

// Public computed signals for components to use
public readonly cards = computed(() => this.cardsState());
public readonly selectedCard = computed(() => this.selectedCardState());
public readonly loading = computed(() => this.loadingState());
public readonly error = computed(() => this.errorState());

// Cache management
private cardCache = new Map<string, DigitalCard>();
private cacheTimestamps = new Map<string, number>();
private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minute
  
  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private auth: Auth
  ) {
console.log('🃏 Cards service initialized with state management'); // MODIFY this line    
  }

  /**
   * Transform error into business-friendly message
   */
  private handleBusinessError(error: any, operation: string): BusinessError {
    console.error(`Business error in ${operation}:`, error);

    // Handle HTTP errors
    if (error.status) {
      switch (error.status) {
        case 400:
          return {
            type: 'validation',
            title: 'Invalid Information',
            message: 'Please check your input and try again.',
            details: error.error?.message || 'The information provided is not valid.',
            code: 'VALIDATION_ERROR'
          };
        
        case 401:
          return {
            type: 'auth',
            title: 'Authentication Required',
            message: 'Please log in again to continue.',
            details: 'Your session has expired or you are not authorized to perform this action.',
            code: 'AUTH_ERROR'
          };
        
        case 403:
          return {
            type: 'auth',
            title: 'Access Denied',
            message: 'You don\'t have permission to perform this action.',
            details: 'Please contact your administrator if you believe this is an error.',
            code: 'PERMISSION_ERROR'
          };
        
        case 409:
          return {
            type: 'business',
            title: 'Already Exists',
            message: 'A vCard with this handler already exists.',
            details: 'Please choose a different handler or contact support if this is your card.',
            code: 'DUPLICATE_HANDLER'
          };
        
        case 422:
          return {
            type: 'validation',
            title: 'Invalid Data',
            message: 'Some of the information provided is incorrect.',
            details: error.error?.message || 'Please review your input and correct any errors.',
            code: 'VALIDATION_ERROR'
          };
        
        case 429:
          return {
            type: 'business',
            title: 'Too Many Requests',
            message: 'You\'ve reached the limit for creating vCards.',
            details: 'Please wait a moment before trying again or upgrade your plan.',
            code: 'RATE_LIMIT'
          };
        
        case 500:
          return {
            type: 'server',
            title: 'Service Temporarily Unavailable',
            message: 'We\'re experiencing technical difficulties.',
            details: 'Our team has been notified. Please try again in a few minutes.',
            code: 'SERVER_ERROR'
          };
        
        case 503:
          return {
            type: 'server',
            title: 'Service Under Maintenance',
            message: 'The service is temporarily under maintenance.',
            details: 'We\'ll be back shortly. Thank you for your patience.',
            code: 'MAINTENANCE'
          };
        
        default:
          return {
            type: 'network',
            title: 'Connection Problem',
            message: 'Unable to connect to our servers.',
            details: 'Please check your internet connection and try again.',
            code: 'NETWORK_ERROR'
          };
      }
    }

    // Handle network errors
    if (error.name === 'TimeoutError' || error.message?.includes('timeout')) {
      return {
        type: 'network',
        title: 'Request Timeout',
        message: 'The request took too long to complete.',
        details: 'Please check your internet connection and try again.',
        code: 'TIMEOUT_ERROR'
      };
    }

    // Handle specific business logic errors
    if (error.message?.includes('handler')) {
      return {
        type: 'validation',
        title: 'Invalid Handler',
        message: 'The chosen handler is not available.',
        details: 'Please choose a different handler. Handlers must be unique and contain only letters, numbers, and underscores.',
        code: 'INVALID_HANDLER'
      };
    }

    if (error.message?.includes('email')) {
      return {
        type: 'validation',
        title: 'Invalid Email',
        message: 'Please enter a valid email address.',
        details: 'The email format is incorrect or the email is already in use.',
        code: 'INVALID_EMAIL'
      };
    }

    if (error.message?.includes('phone')) {
      return {
        type: 'validation',
        title: 'Invalid Phone Number',
        message: 'Please enter a valid phone number.',
        details: 'The phone number format is incorrect.',
        code: 'INVALID_PHONE'
      };
    }

    // Default error
    return {
      type: 'business',
      title: 'Unexpected Error',
      message: 'Something went wrong while processing your request.',
      details: error.message || 'Please try again or contact support if the problem persists.',
      code: 'UNKNOWN_ERROR'
    };
  }

  /**
   * Get HTTP headers with authorization token
   */
  private async getHeaders(): Promise<HttpHeaders> {
    const user = this.authService.currentUser;
    let headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });

    if (user) {
      try {
        // Get the Firebase ID token from the Firebase User object
        const firebaseUser = this.auth.currentUser;
        if (firebaseUser) {
          const token = await firebaseUser.getIdToken();
          headers = headers.set('Authorization', `Bearer ${token}`);
        }
      } catch (error) {
        console.error('Error getting auth token:', error);
      }
    }

    return headers;
  }

  /**
   * Get current user's cards
   * GET /api/cards/me
   */
  // REPLACE the entire getUserCards method with this:
async getUserCards(forceRefresh = false): Promise<DigitalCard[]> {
  // Return cached data if available and not forcing refresh
  if (!forceRefresh && this.cardsState().length > 0) {
    console.log('📦 Returning cached cards:', this.cardsState().length);
    return this.cardsState();
  }

  try {
    this.loadingState.set(true);
    this.errorState.set(null);
    console.log('🔍 Fetching user cards from API...');
    
    const headers = await this.getHeaders();
    const response = await this.http.get<ApiResponse<DigitalCard[]>>(
      `${this.baseUrl}/cards/me`,
      { headers }
    ).toPromise();

    if (response?.success && response.data) {
      console.log('✅ Cards fetched successfully:', response.data.length);
      
      // UPDATE STATE
      this.cardsState.set(response.data);
      
      // UPDATE CACHE
      response.data.forEach(card => {
        this.cardCache.set(card.id, card);
        this.cacheTimestamps.set(card.id, Date.now());
      });
      
      return response.data;
    } else {
      console.warn('⚠️ No cards found or API returned empty response');
      this.cardsState.set([]);
      return [];
    }
  } catch (error: any) {
    console.error('❌ Error fetching user cards:', error);
    const errorMessage = error.message || 'Failed to fetch cards';
    this.errorState.set(errorMessage);
    throw new Error(errorMessage);
  } finally {
    this.loadingState.set(false);
  }
}
  /**
   * Get card by ID
   * GET /api/cards/{cardId}
   */
 // REPLACE the entire getCardById method with this:
async getCardById(cardId: string, forceRefresh = false): Promise<DigitalCard> {
  // Check cache first
  if (!forceRefresh && this.isCardCacheValid(cardId)) {
    const cachedCard = this.cardCache.get(cardId)!;
    console.log('📦 Returning cached card:', cardId);
    this.selectedCardState.set(cachedCard);
    return cachedCard;
  }

  // Check if card exists in current cards state
  const existingCard = this.cardsState().find(card => card.id === cardId);
  if (!forceRefresh && existingCard) {
    console.log('📦 Returning card from state:', cardId);
    this.selectedCardState.set(existingCard);
    return existingCard;
  }

  try {
    this.loadingState.set(true);
    this.errorState.set(null);
    console.log('🔍 Fetching card by ID from API:', cardId);
    
    const headers = await this.getHeaders();
    const response = await this.http.get<ApiResponse<DigitalCard>>(
      `${this.baseUrl}/cards/${cardId}`,
      { headers }
    ).toPromise();

    if (response?.success && response.data) {
      console.log('✅ Card fetched successfully:', response.data);
      
      // UPDATE CACHE AND STATE
      this.cardCache.set(cardId, response.data);
      this.cacheTimestamps.set(cardId, Date.now());
      this.selectedCardState.set(response.data);
      
      // UPDATE CARDS ARRAY IF IT EXISTS THERE
      const cards = this.cardsState();
      const index = cards.findIndex(c => c.id === cardId);
      if (index >= 0) {
        const updatedCards = [...cards];
        updatedCards[index] = response.data;
        this.cardsState.set(updatedCards);
      }
      
      return response.data;
    } else {
      console.warn('⚠️ Card not found');
      throw new Error('Card not found');
    }
  } catch (error: any) {
    console.error('❌ Error fetching card by ID:', error);
    const errorMessage = error.message || 'Failed to fetch card';
    this.errorState.set(errorMessage);
    throw new Error(errorMessage);
  } finally {
    this.loadingState.set(false);
  }
}

  /**
   * Create a new card
   * POST /api/cards
   */
  async createCard(
  cardData: CardCreateRequest
): Promise<{ success: true; data: DigitalCard } | { success: false; error: BusinessError }> {
  try {
    console.log('🔨 Creating new vCard:', cardData);

    const headers = await this.getHeaders();

    // ✅ Use the server's expected field names directly
    const createRequest: CardCreateRequest = {
      contactName: cardData.contactName,
      contactPhone: cardData.contactPhone,
      handler: cardData.handler,
      contactEmail: cardData.contactEmail,
      language: cardData.language || 'en',
      type: cardData.type || 'Business',
      industry: cardData.industry || 'General',
      businessName: cardData.businessName,
    };

    // Log the payload
    console.log('📤 Sending request payload:', JSON.stringify(createRequest, null, 2));

    const response = await this.http
      .post<ApiResponse<DigitalCard>>(`${this.baseUrl}/cards`, createRequest, {
        headers,
        observe: 'response',
      })
      .toPromise();

    // Success block
    if (response?.body?.success && response.body.data) {
      console.log('✅ vCard created successfully:', response.body.data);
      console.log('📥 Create response status:', response?.status);
      console.log('📥 Create response body:', response?.body);

      // Add to local state
      this.addCardToState(response.body.data);

      return { success: true, data: response.body.data };
    }

    // Failure block
    console.error('❌ Failed to create vCard:', {
      status: response?.status,
      message: response?.body?.message || response?.body?.error,
      body: response?.body,
    });

    const businessError = this.handleBusinessError(
      {
        message: response?.body?.message || response?.body?.error || 'Card creation failed',
        status: response?.status || 400,
      },
      'createCard'
    );

    return { success: false, error: businessError };
  } catch (error) {
    console.error('❌ Error creating vCard:', error);
    const businessError = this.handleBusinessError(error, 'createCard');
    return { success: false, error: businessError };
  }
}


  /**
   * Generate a URL-friendly handler from the full name
   */
  private generateHandler(fullName: string): string {
    return fullName
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, '')
      .replace(/\s+/g, '')
      .substring(0, 20) + '_' + Date.now().toString().substring(-4);
  }

  /**
   * Update an existing card
   * PUT /api/cards/{cardId}
   */
  /**
 * Update card personal info
 */
// ADD these new methods:
updateCardInState(updatedCard: DigitalCard): void {
  console.log('🔄 Updating card in state:', updatedCard.id);
  
  // Update cache
  this.cardCache.set(updatedCard.id, updatedCard);
  this.cacheTimestamps.set(updatedCard.id, Date.now());
  
  // Update selected card if it's the same
  if (this.selectedCardState()?.id === updatedCard.id) {
    this.selectedCardState.set(updatedCard);
  }
  
  // Update cards array
  const cards = this.cardsState();
  const index = cards.findIndex(c => c.id === updatedCard.id);
  if (index >= 0) {
    const updatedCards = [...cards];
    updatedCards[index] = updatedCard;
    this.cardsState.set(updatedCards);
  }
}

addCardToState(newCard: DigitalCard): void {
  console.log('➕ Adding card to state:', newCard.id);
  
  this.cardCache.set(newCard.id, newCard);
  this.cacheTimestamps.set(newCard.id, Date.now());
  this.cardsState.update(cards => [...cards, newCard]);
}

removeCardFromState(cardId: string): void {
  console.log('🗑️ Removing card from state:', cardId);
  
  this.cardCache.delete(cardId);
  this.cacheTimestamps.delete(cardId);
  this.cardsState.update(cards => cards.filter(c => c.id !== cardId));
  
  if (this.selectedCardState()?.id === cardId) {
    this.selectedCardState.set(null);
  }
}

setSelectedCard(card: DigitalCard | null): void {
  this.selectedCardState.set(card);
  if (card) {
    // Update cache when manually setting selected card
    this.cardCache.set(card.id, card);
    this.cacheTimestamps.set(card.id, Date.now());
  }
}

clearState(): void {
  console.log('🧹 Clearing all state');
  this.cardsState.set([]);
  this.selectedCardState.set(null);
  this.loadingState.set(false);
  this.errorState.set(null);
  this.cardCache.clear();
  this.cacheTimestamps.clear();
}

// Helper method to check cache validity
private isCardCacheValid(cardId: string): boolean {
  if (!this.cardCache.has(cardId)) return false;
  
  const timestamp = this.cacheTimestamps.get(cardId);
  if (!timestamp) return false;
  
  return (Date.now() - timestamp) < this.CACHE_DURATION;
}
// MODIFY updateCardPersonalInfo - add these lines after "return true;":
async updateCardPersonalInfo(cardId: string, personalInfo: Partial<PersonalInfo>): Promise<boolean> {
  const success = await this.putUpdate(`${this.baseUrl}/cards/${cardId}/personal-info`, personalInfo, 'personal info');
  
  // ADD these lines:
  if (success) {
    try {
      await this.getCardById(cardId, true);
    } catch (error) {
      console.error('Failed to refresh card after update:', error);
    }
  }
  
  return success;
}

// MODIFY updateCardBusinessInfo - add these lines after "return true;":
async updateCardBusinessInfo(cardId: string, businessInfo: Partial<BusinessInfo>): Promise<boolean> {
  const success = await this.putUpdate(`${this.baseUrl}/cards/${cardId}/business-info`, businessInfo, 'business info');
  
  // ADD these lines:
  if (success) {
    try {
      await this.getCardById(cardId, true);
    } catch (error) {
      console.error('Failed to refresh card after update:', error);
    }
  }
  
  return success;
}

// MODIFY updateCardContact - add these lines after "return true;":
async updateCardContact(cardId: string, contactData: Partial<Contact>): Promise<boolean> {
  const success = await this.putUpdate(`${this.baseUrl}/cards/${cardId}/contact`, contactData, 'contact');
  
  // ADD these lines:
  if (success) {
    try {
      await this.getCardById(cardId, true);
    } catch (error) {
      console.error('Failed to refresh card after update:', error);
    }
  }
  
  return success;
}

/**
 * Update card socials
 */
async updateCardSocialLinks(cardId: string, socialLinks: social_links[]): Promise<boolean> {
  try {
    console.log('🌐 Updating card social links:', cardId, socialLinks);

    const headers = await this.getHeaders();
    const response = await this.http.put<ApiResponse<void>>(
      `${this.baseUrl}/cards/${cardId}/socials`,
      { socialLinks },
      { headers }
    ).toPromise();

    if (response?.success) {
      console.log('✅ Social links updated successfully');
      // Refresh card to get latest data
      await this.getCardById(cardId, true);
      return true;
    } else {
  console.error('❌ Failed to update social links:', response?.message || response?.error, response);
      throw new Error(response?.message || response?.error || 'Failed to update social links');
    }
  } catch (error) {
  const errMsg = (error && (error as any).message) ? (error as any).message : String(error);
  console.error('❌ Error updating social links:', errMsg, error);
    // Surface the error so caller can handle it; include response body if available
    throw error;
  }
}

/**
 * Generic PUT helper
 */
private async putUpdate<T>(url: string, body: T, label: string): Promise<boolean> {
  try {
    const headers = await this.getHeaders();
    const response = await this.http.put<ApiResponse<void>>(url, body, { headers }).toPromise();
    if (response?.success) {
      console.log(`✅ ${label} updated successfully`);
      return true;
    } else {
      console.error(`❌ Failed to update ${label}:`, response?.message || response?.error);
      throw new Error(response?.message || response?.error || `Failed to update ${label}`);
    }
  } catch (error) {
    console.error(`❌ Error updating ${label}:`, error);
    throw error;
  }
}


  /**
   * Delete a card
   * DELETE /api/cards/{cardId}
   */
  async deleteCard(cardId: string): Promise<boolean> {
    try {
      console.log('🗑️ Deleting card:', cardId);
      
      const headers = await this.getHeaders();
      const response = await this.http.delete<ApiResponse<void>>(
        `${this.baseUrl}/cards/${cardId}`,
        { headers }
      ).toPromise();

      if (response?.success) {
        console.log('✅ Card deleted successfully');
        this.removeCardFromState(cardId);
        return true;
      } else {
        console.error('❌ Failed to delete card:', response?.message || response?.error);
        throw new Error(response?.message || response?.error || 'Failed to delete card');
      }
    } catch (error) {
      console.error('❌ Error deleting card:', error);
      throw error;
    }
  }

  // =============================================================================
  // INCREMENTAL UPDATE ENDPOINTS
  // =============================================================================

  /**
   * Update card contact details
   * PUT /api/cards/{cardId}/contact
   */
  
  /**
   * Update card address
   * PUT /api/cards/{cardId}/address
   */
 

  /**
   * Update card social links
   * PUT /api/cards/{cardId}/socials
   */
 

  /**
   * Update card quick links
   * PUT /api/cards/{cardId}/quick-links
   */
  async updateCardQuickLinks(cardId: string, quick_links: quick_links[]): Promise<boolean> {
  try {
    console.log('⚡ Updating card quick links:', cardId, quick_links);

    const headers = await this.getHeaders();
    const response = await this.http.put<ApiResponse<void>>(
      `${this.baseUrl}/cards/${cardId}/quick-links`,
      { quickLinks: quick_links }, // ✅ FIXED key
      { headers }
    ).toPromise();

    if (response?.success) {
      console.log('✅ Quick links updated successfully');
      await this.getCardById(cardId, true);
      return true;
    } else {
      throw new Error(response?.message || response?.error || 'Failed to update quick links');
    }
  } catch (error) {
    console.error('❌ Error updating quick links:', error);
    throw error;
  }
}


  /**
   * Update card profile image
   * PUT /api/cards/{cardId}/image
   */
  async updateCardImage(cardId: string, imageData: { imageUrl?: string; imageBase64?: string }): Promise<boolean> {
    try {
      console.log('🖼️ Updating card image:', cardId);
      
      const headers = await this.getHeaders();
      const response = await this.http.put<ApiResponse<void>>(
        `${this.baseUrl}/cards/${cardId}/image`,
        imageData,
        { headers }
      ).toPromise();

      if (response?.success) {
        console.log('✅ Image updated successfully');
        return true;
      } else {
        console.error('❌ Failed to update image:', response?.message || response?.error);
        throw new Error(response?.message || response?.error || 'Failed to update image');
      }
    } catch (error) {
      console.error('❌ Error updating image:', error);
      throw error;
    }
  }
  

  /**
   * Update card business information
   * PUT /api/cards/{cardId}/business-info
   */
  

  /**
   * Update card theme colors
   * PUT /api/cards/{cardId}/theme
   */
  async updateCardTheme(cardId: string, themeColors: Partial<ThemeColors>): Promise<boolean> {
    try {
      console.log('🎨 Updating card theme:', cardId, themeColors);
      
      const headers = await this.getHeaders();
      const response = await this.http.put<ApiResponse<void>>(
        `${this.baseUrl}/cards/${cardId}/theme`,
        themeColors,
        { headers }
      ).toPromise();

      if (response?.success) {
        console.log('✅ Theme updated successfully');
        return true;
      } else {
        console.error('❌ Failed to update theme:', response?.message || response?.error);
        throw new Error(response?.message || response?.error || 'Failed to update theme');
      }
    } catch (error) {
      console.error('❌ Error updating theme:', error);
      throw error;
    }
  }

  // Method to update card entities
  async updatecardEntities(cardId: string, entitiesData: { [key: string]: any }): Promise<boolean> {
    try {
      console.log('🏢 Updating card entities:', cardId, entitiesData);
      const headers = await this.getHeaders();
      const response = await this.http.put<ApiResponse<void>>(
        `${this.baseUrl}/cards/${cardId}/theme`,
        entitiesData,
        { headers }
      ).toPromise();
      if (response?.success) {
        console.log('✅ Card entities updated successfully');
        return true;
      } else {
        console.error('❌ Failed to update card entities:', response?.message || response?.error);
        throw new Error(response?.message || response?.error || 'Failed to update card entities');
      }
    } catch (error) {
      console.error('❌ Error updating card entities:', error);
      throw error;
    }   
  }
    //  Method to update card leaders
  async updateCardLeaders(cardId: string, leadersData: { [key: string]: any }): Promise<boolean> {
    try {
      console.log('👥 Updating card leaders:', cardId, leadersData);
      const headers = await this.getHeaders();
      const response = await this.http.put<ApiResponse<void>>(
        `${this.baseUrl}/cards/${cardId}/theme`,  
        leadersData,
        { headers }
      ).toPromise();
      if (response?.success) {
        console.log('✅ Card leaders updated successfully');
        return true;
      } else {
        console.error('❌ Failed to update card leaders:', response?.message || response?.error);
        throw new Error(response?.message || response?.error || 'Failed to update card leaders');
      }
    } catch (error) {
      console.error('❌ Error updating card leaders:', error);
      throw error;
    }
  }
  /// method to update card permissions
  async updateCardPermissions(cardId: string, permissionsData: { [key: string]: any }): Promise<boolean> {
    try {
      console.log('🔒 Updating card permissions:', cardId, permissionsData);
      const headers = await this.getHeaders();
      const response = await this.http.put<ApiResponse<void>>(
        `${this.baseUrl}/cards/${cardId}/theme`,
        permissionsData,
        { headers }
      ).toPromise();
      if (response?.success) {
        console.log('✅ Card permissions updated successfully');
        return true;
      }
      else {
        console.error('❌ Failed to update card permissions:', response?.message || response?.error);
        throw new Error(response?.message || response?.error || 'Failed to update card permissions');
      }
    } catch (error) {
      console.error('❌ Error updating card permissions:', error);
      throw error;
    }
  }

// method to update card branchess
  async updateCardBranchess(cardId: string, branchesData: { [key: string]: any }): Promise<boolean> {
    try {
      console.log('🌿 Updating card branches:', cardId, branchesData);
      const headers = await this.getHeaders();
      const response = await this.http.put<ApiResponse<void>>(
        `${this.baseUrl}/cards/${cardId}/theme`,
        branchesData,
        { headers }
      ).toPromise();
      if (response?.success) {
        console.log('✅ Card branches updated successfully');
        return true;
      } else {
        console.error('❌ Failed to update card branches:', response?.message || response?.error);
        throw new Error(response?.message || response?.error || 'Failed to update card branches');
      }
    } catch (error) {
      console.error('❌ Error updating card branches:', error);
      throw error;
    }
  }


  /**
   * Update Google Maps location
   * PUT /api/cards/{cardId}/gmap
   */
  async updateCardGoogleMap(cardId: string, gmapData: { latitude: number; longitude: number; placeName?: string }): Promise<boolean> {
    try {
      console.log('�️ Updating card Google Maps location:', cardId, gmapData);
      
      const headers = await this.getHeaders();
      const response = await this.http.put<ApiResponse<void>>(
        `${this.baseUrl}/cards/${cardId}/gmap`,
        gmapData,
        { headers }
      ).toPromise();

      if (response?.success) {
        console.log('✅ Google Maps location updated successfully');
        return true;
      } else {
        console.error('❌ Failed to update Google Maps location:', response?.message || response?.error);
        throw new Error(response?.message || response?.error || 'Failed to update Google Maps location');
      }
    } catch (error) {
      console.error('❌ Error updating Google Maps location:', error);
      throw error;
    }
  }

  // =============================================================================
  // PAYMENT METHODS UPDATE ENDPOINTS
  // =============================================================================

  /**
   * Update UPI payment details
   * PUT /api/cards/{cardId}/payments/upi
   */
  async updateCardUPIPayment(cardId: string, upiData: Partial<UPIPayment>): Promise<boolean> {
    try {
      console.log('💳 Updating card UPI payment:', cardId, upiData);
      
      const headers = await this.getHeaders();
      const response = await this.http.put<ApiResponse<void>>(
        `${this.baseUrl}/cards/${cardId}/payments/upi`,
        upiData,
        { headers }
      ).toPromise();

      if (response?.success) {
        console.log('✅ UPI payment updated successfully');
        return true;
      } else {
        console.error('❌ Failed to update UPI payment:', response?.message || response?.error);
        throw new Error(response?.message || response?.error || 'Failed to update UPI payment');
      }
    } catch (error) {
      console.error('❌ Error updating UPI payment:', error);
      throw error;
    }
  }

  /**
   * Update NEFT bank account details
   * PUT /api/cards/{cardId}/payments/neft
   */
  async updateCardNEFTPayment(cardId: string, bankData: Partial<BankDetails>): Promise<boolean> {
    try {
      console.log('🏦 Updating card NEFT payment:', cardId, bankData);
      
      const headers = await this.getHeaders();
      const response = await this.http.put<ApiResponse<void>>(
        `${this.baseUrl}/cards/${cardId}/payments/neft`,
        bankData,
        { headers }
      ).toPromise();

      if (response?.success) {
        console.log('✅ NEFT payment updated successfully');
        return true;
      } else {
        console.error('❌ Failed to update NEFT payment:', response?.message || response?.error);
        throw new Error(response?.message || response?.error || 'Failed to update NEFT payment');
      }
    } catch (error) {
      console.error('❌ Error updating NEFT payment:', error);
      throw error;
    }
  }

  /**
   * Update payment gateway credentials
   * PUT /api/cards/{cardId}/payments/gateway
   */
  async updateCardPaymentGateway(cardId: string, gatewayData: Partial<PaymentGateway>): Promise<boolean> {
    try {
      console.log('🔐 Updating card payment gateway:', cardId, gatewayData);
      
      const headers = await this.getHeaders();
      const response = await this.http.put<ApiResponse<void>>(
        `${this.baseUrl}/cards/${cardId}/payments/gateway`,
        gatewayData,
        { headers }
      ).toPromise();

      if (response?.success) {
        console.log('✅ Payment gateway updated successfully');
        return true;
      } else {
        console.error('❌ Failed to update payment gateway:', response?.message || response?.error);
        throw new Error(response?.message || response?.error || 'Failed to update payment gateway');
      }
    } catch (error) {
      console.error('❌ Error updating payment gateway:', error);
      throw error;
    }
  }

  // =============================================================================
  // SERVICES MANAGEMENT ENDPOINTS
  // =============================================================================

  /**
   * Add a service to a card
   * POST /api/cards/{cardId}/services
   */
  async addCardService(cardId: string, service: Omit<Service, 'id'>): Promise<Service | null> {
    try {
      console.log('⚙️ Adding service to card:', cardId, service);
      
      const headers = await this.getHeaders();
      const response = await this.http.post<ApiResponse<Service>>(
        `${this.baseUrl}/cards/${cardId}/services`,
        service,
        { headers }
      ).toPromise();

      if (response?.success && response.data) {
        console.log('✅ Service added successfully');
        return response.data;
      } else {
        console.error('❌ Failed to add service:', response?.message || response?.error);
        throw new Error(response?.message || response?.error || 'Failed to add service');
      }
    } catch (error) {
      console.error('❌ Error adding service:', error);
      throw error;
    }
  }

  /**
   * Get services for a card
   * GET /api/cards/{cardId}/services
   */
  async getCardServices(cardId: string): Promise<Service[]> {
    try {
      console.log('🔍 Fetching services for card:', cardId);
      
      const headers = await this.getHeaders();
      const response = await this.http.get<ApiResponse<Service[]>>(
        `${this.baseUrl}/cards/${cardId}/services`,
        { headers }
      ).toPromise();

      if (response?.success && response.data) {
        console.log('✅ Services fetched successfully');
        return response.data;
      } else {
        console.warn('⚠️ No services found');
        return [];
      }
    } catch (error) {
      console.error('❌ Error fetching services:', error);
      throw error;
    }
  }

  /**
   * Update a service
   * PUT /api/cards/{cardId}/services/{serviceId}
   */
  async updateCardService(cardId: string, serviceId: string, service: Partial<Service>): Promise<boolean> {
    try {
      console.log('🔄 Updating service:', cardId, serviceId, service);
      
      const headers = await this.getHeaders();
      const response = await this.http.put<ApiResponse<void>>(
        `${this.baseUrl}/cards/${cardId}/services/${serviceId}`,
        service,
        { headers }
      ).toPromise();

      if (response?.success) {
        console.log('✅ Service updated successfully');
        return true;
      } else {
        console.error('❌ Failed to update service:', response?.message || response?.error);
        throw new Error(response?.message || response?.error || 'Failed to update service');
      }
    } catch (error) {
      console.error('❌ Error updating service:', error);
      throw error;
    }
  }

  /**
   * Delete a service
   * DELETE /api/services/{serviceId}
   */
  async deleteCardService(serviceId: string): Promise<boolean> {
    try {
      console.log('�️ Deleting service:', serviceId);
      
      const headers = await this.getHeaders();
      const response = await this.http.delete<ApiResponse<void>>(
        `${this.baseUrl}/services/${serviceId}`,
        { headers }
      ).toPromise();

      if (response?.success) {
        console.log('✅ Service deleted successfully');
        return true;
      } else {
        console.error('❌ Failed to delete service:', response?.message || response?.error);
        throw new Error(response?.message || response?.error || 'Failed to delete service');
      }
    } catch (error) {
      console.error('❌ Error deleting service:', error);
      throw error;
    }
  }

  // =============================================================================
  // PRODUCTS MANAGEMENT ENDPOINTS
  // =============================================================================

  /**
   * Add a product to a card
   * POST /api/cards/{cardId}/products
   */
  async addCardProduct(cardId: string, product: Omit<Product, 'id'>): Promise<Product | null> {
    try {
      console.log('📦 Adding product to card:', cardId, product);
      
      const headers = await this.getHeaders();
      const response = await this.http.post<ApiResponse<Product>>(
        `${this.baseUrl}/cards/${cardId}/products`,
        product,
        { headers }
      ).toPromise();

      if (response?.success && response.data) {
        console.log('✅ Product added successfully');
        return response.data;
      } else {
        console.error('❌ Failed to add product:', response?.message || response?.error);
        throw new Error(response?.message || response?.error || 'Failed to add product');
      }
    } catch (error) {
      console.error('❌ Error adding product:', error);
      throw error;
    }
  }

  /**
   * Update a product
   * PUT /api/cards/{cardId}/products/{productId}
   */
  async updateCardProduct(cardId: string, productId: string, product: Partial<Product>): Promise<boolean> {
    try {
      console.log('🔄 Updating product:', cardId, productId, product);
      
      const headers = await this.getHeaders();
      const response = await this.http.put<ApiResponse<void>>(
        `${this.baseUrl}/cards/${cardId}/products/${productId}`,
        product,
        { headers }
      ).toPromise();

      if (response?.success) {
        console.log('✅ Product updated successfully');
        return true;
      } else {
        console.error('❌ Failed to update product:', response?.message || response?.error);
        throw new Error(response?.message || response?.error || 'Failed to update product');
      }
    } catch (error) {
      console.error('❌ Error updating product:', error);
      throw error;
    }
  }

  // =============================================================================
  // EVENTS MANAGEMENT ENDPOINTS
  // =============================================================================

  /**
   * Add event to specific card
   * POST /api/cards/{cardId}/events
   */
  async addCardEvent(cardId: string, event: Omit<Event, 'id'>): Promise<Event | null> {
    try {
      console.log('📅 Adding event to card:', cardId, event);
      
      const headers = await this.getHeaders();
      const response = await this.http.post<ApiResponse<Event>>(
        `${this.baseUrl}/cards/${cardId}/events`,
        event,
        { headers }
      ).toPromise();

      if (response?.success && response.data) {
        console.log('✅ Event added successfully');
        return response.data;
      } else {
        console.error('❌ Failed to add event:', response?.message || response?.error);
        throw new Error(response?.message || response?.error || 'Failed to add event');
      }
    } catch (error) {
      console.error('❌ Error adding event:', error);
      throw error;
    }
  }

  /**
   * Get events for a card
   * GET /api/cards/{cardId}/events
   */
  async getCardEvents(cardId: string): Promise<Event[]> {
    try {
      console.log('🔍 Fetching events for card:', cardId);
      
      const headers = await this.getHeaders();
      const response = await this.http.get<ApiResponse<Event[]>>(
        `${this.baseUrl}/cards/${cardId}/events`,
        { headers }
      ).toPromise();

      if (response?.success && response.data) {
        console.log('✅ Events fetched successfully');
        return response.data;
      } else {
        console.warn('⚠️ No events found');
        return [];
      }
    } catch (error) {
      console.error('❌ Error fetching events:', error);
      throw error;
    }
  }


  /**
 * Get card image info
 * GET /api/card-image/card/{cardId}
 */
async getCardImage(cardId: string): Promise<CardImage | null> {
  try {
    console.log('🔍 Fetching card image for cardId:', cardId);

    if (!cardId) {
      throw new Error('cardId is required');
    }

    const headers = await this.getHeaders();
    const response = await this.http.get<ApiResponse<CardImage>>(
      `${this.baseUrl}/card-image/card/${cardId}`,
      { headers }
    ).toPromise();

    if (response?.success && response.data) {
      console.log('✅ Card image fetched successfully');
      return response.data;
    } else {
      console.warn('⚠️ No card image found');
      return null;
    }
  } catch (error) {
    console.error('❌ Error fetching card image:', error);
    throw error;
  }
}

// inside CardsService
getCardThemeFromState(cardId: string): ThemeColors | null {
  console.log('[CardsService] getCardThemeFromState called for:', cardId);

  // prefer cache, fallback to cardsState
  const card = (this.cardCache.get(cardId) ?? this.cardsState().find(c => c.id === cardId)) as any;
  if (!card) {
    console.warn('[CardsService] Card not found in state/cache for id:', cardId);
    return null;
  }

  // normalize hex: accept "#RRGGBB", "RRGGBB", "#AARRGGBB", "AARRGGBB", or "#RGB"
  const normalizeHex = (v: any): string | null => {
    if (v === undefined || v === null) return null;
    let s = String(v).trim();
    if (!s) return null;
    if (!s.startsWith('#')) s = `#${s}`;
    const hex = s.replace(/^#/, '');
    if (/^[0-9A-Fa-f]{3}$/.test(hex)) return `#${hex}`;
    if (/^[0-9A-Fa-f]{6}$/.test(hex)) return `#${hex.toUpperCase()}`;
    if (/^[0-9A-Fa-f]{8}$/.test(hex)) return `#${hex.toUpperCase()}`; // keep 8-char if present
    return null;
  };

  const primary = normalizeHex(card.primary_color_1);
  const secondary = normalizeHex(card.primary_color_2);

  if (!primary && !secondary) {
    console.warn('[CardsService] No primary colors found on card:', cardId);
    return null;
  }

  const result: ThemeColors = {
    primary: primary ?? (secondary ?? '#7b2ff7'),
    secondary: secondary ?? (primary ?? '#f107a3'),
    accent: secondary ?? primary ?? '#ffffff',
    // optional convenience fields
    gradientStart: primary ?? undefined,
    gradientEnd: secondary ?? undefined
  };

  console.log('[CardsService] derived theme for', cardId, result);
  return result;
}


}
export type { DigitalCard };